/**
 * Pure decision helper for the parked "focus this field" request that the
 * global search publishes on `fieldFocusBus`.
 *
 * Why the request is parked at all: the search can point at a field in another
 * farming unit. It switches the unit through Redux, which reaches the Farm View
 * a tick later — so the request has to wait for the unit it belongs to before
 * it can be applied.
 *
 * Why this is pure: the effect that consumes it calls `setState`, and the thing
 * that makes that safe is convergence — every request is resolved in a single
 * pass and then cleared, and a cleared request is a no-op. That property is
 * worth testing directly rather than reasoning about inside a component.
 */

/**
 * @param {{fieldId: string, farmingUnitId: string, openDetails: boolean}|null} pendingFocus
 * @param {string|undefined} farmingUnitId - the unit currently on screen
 * @param {Object<string, object>|undefined} fields - that unit's fields
 * @returns {{field: object|null, shouldClear: boolean}}
 *   `field` is the field to select, if it is here.
 *   `shouldClear` is true once the request has been resolved — meaning the
 *   caller must drop it, so the next pass does nothing.
 */
const resolveFieldFocus = (pendingFocus, farmingUnitId, fields) => {
  // Nothing parked, or its unit is not on screen yet: keep waiting. Returning
  // shouldClear:false here is what lets a cross-unit request survive until the
  // unit catches up.
  if (!pendingFocus || pendingFocus.farmingUnitId !== farmingUnitId) {
    return { field: null, shouldClear: false };
  }

  // The right unit is on screen, so this request is resolved either way: the
  // field is here and gets selected, or it is not and the request is dropped
  // rather than left to fire later.
  return { field: fields?.[pendingFocus.fieldId] ?? null, shouldClear: true };
};

/**
 * What Farm View does with a field link (`?distributionId&farmingUnitId
 * [&fieldId]`, e.g. from Monday comments, N-281). The link arrives before the
 * Farm View has anything to focus, so it cannot go through fieldFocusBus (no
 * replay): Farm View selects the unit itself and parks the focus, which
 * resolveFieldFocus applies once that unit is on screen.
 *
 * A unit missing from the user's distributions is ignored, so a link the user
 * has no access to never parks a focus that would wait forever.
 *
 * @param {{distributionId: string, farmingUnitId: string,
 *   fieldId: string|null}|null} link - viewPaths.getFarmViewDeepLink result
 * @param {Object<string, {farming_units?: Object}>|undefined} distributions
 * @returns {{selectUnit: boolean, focus: object|null}|null} null when there
 *   is no link.
 */
const getDeepLinkIntake = (link, distributions) => {
  if (!link) return null;
  const isKnownUnit = Boolean(
    distributions?.[link.distributionId]?.farming_units?.[link.farmingUnitId],
  );
  const focus =
    isKnownUnit && link.fieldId
      ? {
          fieldId: link.fieldId,
          farmingUnitId: link.farmingUnitId,
          openDetails: true,
        }
      : null;
  return { selectUnit: isKnownUnit, focus };
};

module.exports = { resolveFieldFocus, getDeepLinkIntake };
